import React, {useCallback, useEffect, useRef, useState} from "react";
import {
  ANNUITANT_TYPE,
  APP_SECTION,
  AVAILABLE_STATES,
  createAnnuitant,
  createJointAnnuitant,
  createJointOwner,
  createOwner,
  createOwnerAnnuitant, ENTITY_TYPE,
  getCommonOccupations,
  getOtherOccupations,
  OWNER_TYPE,
  PARTY,
  PARTY_RELATIONSHIPS,
  typeForParty,
  getReciprocalRelationshipFor, PARTY_RELATIONSHIPS_SELECT, ENABLE_FLORIDA_FLOW, PAGES, getAgeFromDOB
} from "../../../scripts/constants";
import {isValid, mygaSession} from "../../../scripts/myga-session";
import {charLimits, formatting, NassauLog, regex} from "../../../scripts/myga-utils";
import {Col, Container, Form, Row, Tab, Tabs} from "react-bootstrap";
import NSREDatePicker from "../../../components/NSREDatePicker";
import moment from "moment";
import Resources from "../../../Resources/Resources";
import PopoverTooltip from "../../../components/PopoverTooltip";
import AppContentWrapper from "../AppContentWrapper/AppContentWrapper";
import MYGAStrings from "../../../Resources/Content/MYGAStrings";
import {PageManager} from "../../../scripts/page-manager";
import {saveApplication as syncApplication} from "../../../scripts/sync-manager";
import {
  canAddAnnuitant, canAddJointAnnuitant,
  canAddJointOwner,
  canAddOwner, hasAnnuitant, hasJointAnnuitant, hasJointOwner, hasOwner,
  hasOwnerAsIndividual, hasOwnerSameAsAnnuitant, isFL, isIRA,
  isPartyOK,
  isUsingQualifiedFunds, reconcileParties
} from "../../../scripts/validation";
import {v4 as uuidv4} from "uuid";
import {MM_PRODUCT_DTO} from "../../../scripts/myga-model";
import GENERATOR from "../../../selenium-front-end/generator/Generator";
import {getInfoForZip} from "../../../scripts/zip-helper";

function OwnerAnnuitant(props) {

  // Thoughts:
  // - need to know which "party" is being manipulated
  // - need to know if we should display in "cancellable" mode.
  
  const [parties,setParties] = useState([]);
  const [zipConfirm, setZipConfirm] = useState(false);
  const [warningMessage, setWarningMessage] = useState(null);
  const errorMessage = useRef(null);
  const pageForm = useRef(null);
  const dobInput = useRef(null);
  const dobInputValidation = useRef(null);
  const dotInput = useRef(null);
  const dotInputValidation = useRef(null);
  const idExpirationInput = useRef(null);
  const idExpirationInputValidation = useRef(null);
  const ssnInput = useRef(null);
  const relationshipToOwnerInput = useRef(null);
  const relationshipToAnnuitantInput = useRef(null);
  const initialParties = useRef(null);

  const getPartyField = (partyArr, field) => !isValid(partyArr[index]) ? '' : !isValid(field) ? partyArr[index] : partyArr[index][field]
  const getInitialParty = field => getPartyField(initialParties.current, field)
  const party = field => getPartyField(parties, field)
  
  const toElementId = (string) => string.replace(/ /g,'_').toLowerCase();
  
  // let currAppData;
  
  const compAppData = () => {
    // NassauLog(`CompAppData: ${JSON.stringify(currAppData,null,2)}`);
    return {
      //...currAppData,
      ...mygaSession.getCurrentAppData(),
      [APP_SECTION.parties]: parties
    }
  }
  
  const isEditing = mygaSession.getReturnToPage()!==null;
  const index = mygaSession.getCurrentPartyIndex();
  const isOwner = (p=party) => p(PARTY.ownerType) === OWNER_TYPE.primary;
  const isJointOwner = (p=party) => (p(PARTY.ownerType) === OWNER_TYPE.joint);
  const isAnnuitant = (p=party) => (p(PARTY.annuitantType) === ANNUITANT_TYPE.primary);
  const isJointAnnuitant = (p=party) => (p(PARTY.annuitantType) === ANNUITANT_TYPE.joint);
  const isNotAnOwner = (p=party) => (p(PARTY.ownerType) === OWNER_TYPE.none);
  const isNotAnAnnuitant = (p=party) => (p(PARTY.annuitantType) === ANNUITANT_TYPE.none);
  const getOwnerIndex = (ps=parties) => ps.findIndex(p=>(p[PARTY.ownerType]===OWNER_TYPE.primary));
  const getAnnuitantIndex = (ps=parties) => ps.findIndex(p=>(p[PARTY.annuitantType]===ANNUITANT_TYPE.primary));
  
  const compHasOwner = () => hasOwner(compAppData());
  const compHasAnnuitant = () => hasAnnuitant(compAppData());
  const compHasOwnerSameAsAnnuitant = () => hasOwnerSameAsAnnuitant(compAppData());
  const showRelationToOwner = () => !isOwner() && compHasOwner()
  const showRelationToAnnuitant = () => !isAnnuitant() && compHasAnnuitant() && !compHasOwnerSameAsAnnuitant()

  // Initial Load
  useEffect(()=>{
    // either get the requested party or create the most likely needed next one. opinionated.
    // NassauLog(`index: ${index}`);
    // currAppData = {...mygaSession.getCurrentAppData()};
    // NassauLog(`Current App Data (in function): ${JSON.stringify(currAppData,null,2)}`);
    let nextParties = [...mygaSession.getCurrentAppParties()];
    // NassauLog(`index: ${index}, len: ${nextParties.length}`);
    
    // Check to see if we're adding a new party or editing a current one
    if (nextParties.length===index) {
      let initialParty =
            nextParties.length===0 ? createOwnerAnnuitant() :
                !hasOwner() ? createOwner() :
                    !hasAnnuitant() ? createAnnuitant() :
                        !hasJointOwner() && !isUsingQualifiedFunds() ? createJointOwner() :
                            !hasJointAnnuitant() ? createJointAnnuitant() : {};
      
      // give the new party a UUID
      initialParty[PARTY.uid] = uuidv4();
      // NassauLog(`initialParty: ${JSON.stringify(initialParty,null,2)}`);
      nextParties.push(initialParty);
    }
  
    const reconciled = reconcileParties(nextParties);
    if (reconciled) {
      NassauLog('reconciledParties: ' + JSON.stringify(reconciled, null, 2));
      setParties(reconciled);
    } else {
      setParties(nextParties);
    }
  },[]); // eslint-disable-line react-hooks/exhaustive-deps

  // parties update
  useEffect(() => {
    // Update comAppData
    errorMessage.current = '';
    setWarningMessage(null);
    const reconciled = reconcileParties(parties);
    if (reconciled) {
      NassauLog('reconciledParties: ' + JSON.stringify(reconciled, null, 2));
      setParties(reconciled);
    } else {
      if (initialParties.current === null && parties.length !== 0) {
        initialParties.current = parties
        NassauLog('p')
        NassauLog(initialParties.current)
      }
    }
  }, [parties]);

  let validate = async (nextPage) => {
    if (nextPage === PageManager.getPreviousPage()) {
      NassauLog('going back');
      return true;
    }
    
    // clear
    if (ssnInput.current) {
      ssnInput.current.setCustomValidity('');
    }
    if (relationshipToOwnerInput.current) {
      relationshipToOwnerInput.current.setCustomValidity('');
    }
    if (relationshipToAnnuitantInput.current) {
      relationshipToAnnuitantInput.current.setCustomValidity('');
    }
    
    // check the form validity
    if (!pageForm.current.checkValidity()) {
      let errMessage = MYGAStrings.IdDataEntry_ErrorMissingInformation;
      pageForm.current.classList.add('was-validated');
      
      if (party(PARTY.entityType)===ENTITY_TYPE.individual) {
  
        if (!dobInputValidation.current.checkValidity()) {
          NassauLog('dob invalid');
          dobInput.current.setCustomValidity('invalid');
        } else {
          dobInput.current.setCustomValidity('');
        }
  
        if (!idExpirationInputValidation.current.checkValidity()) {
          NassauLog('idExpiration invalid');
          idExpirationInput.current.setCustomValidity('invalid');
        } else {
          idExpirationInput.current.setCustomValidity('');
        }
        if (new Date(party(PARTY.idExpiration)) < new Date()) {
          idExpirationInput.current.setCustomValidity('ID cannot be expired.');
          errMessage = MYGAStrings.IdDateEntry_IDExpired;
        }
      }
      
      else if (party(PARTY.entityType)===ENTITY_TYPE.trust) {
        
        if (!dotInputValidation.current.checkValidity()) {
          NassauLog('dot invalid');
          dotInput.current.setCustomValidity('invalid');
        } else {
          NassauLog('dot invalid');
          dotInput.current.setCustomValidity('');
        }
  
      }
  
        errorMessage.current = errMessage;
      return false;
    }
  
    if (party(PARTY.entityType)===ENTITY_TYPE.individual) {
      if (new Date(party(PARTY.idExpiration)) < new Date()) {
        idExpirationInput.current.setCustomValidity('ID cannot be expired.');
        pageForm.current.classList.add('was-validated');
        errorMessage.current = MYGAStrings.IdDateEntry_IDExpired;
        return false;
      } else {
        idExpirationInput.current.setCustomValidity('');
      }
    }
  
    if (party(PARTY.entityType)===ENTITY_TYPE.trust) {
      NassauLog(`dateOfTrust: ${party(PARTY.dateOfTrust)}`);
      if (new Date(party(PARTY.dateOfTrust)) > new Date()) {
        NassauLog('dateOfTrust invalid');
        //TODO: Add to MYGA strings
        const message = 'Date of Trust cannot be in the future.';
        dotInput.current.setCustomValidity(message);
        pageForm.current.classList.add('was-validated');
        errorMessage.current = message;
        return false;
      } else {
        NassauLog('dateOfTrust is valid');
        dotInput.current.setCustomValidity('');
      }
    }
    
    // check the owner relationship selector
    if (relationshipToOwnerInput.current && party(PARTY.relationshipToOwner)===PARTY_RELATIONSHIPS.none) {
      relationshipToOwnerInput.current.setCustomValidity('Relationship must be selected.');
      pageForm.current.classList.add('was-validated');
      errorMessage.current = MYGAStrings.IdDateEntry_RelationshipInvalid;
      return false;
    }
  
    // check the owner relationship selector
    if (relationshipToAnnuitantInput.current && party(PARTY.relationshipToAnnuitant)===PARTY_RELATIONSHIPS.none) {
      relationshipToAnnuitantInput.current.setCustomValidity('Relationship must be selected.');
      pageForm.current.classList.add('was-validated');
      errorMessage.current = MYGAStrings.IdDateEntry_RelationshipInvalid;
      return false;
    }
  
    // check the SSN, must be unique for all parties
    const ssnCount = parties.reduce((count,p)=>{
      return count + (p[PARTY.ssnOrTaxId]===party()[PARTY.ssnOrTaxId] ? 1 : 0);
    },0);
    if (ssnCount>1) {
      ssnInput.current.setCustomValidity('invalid');
      pageForm.current.classList.add('was-validated');
      errorMessage.current = MYGAStrings.IdDataEntry_SsnOrTaxIdInUse;
      return false;
    }
    
    // test
    NassauLog(`OwnerAnnuitant: isPartyOK=${isPartyOK(party())}`);
    // TODO: ONLY CHECK STATE & ZIP (Do not remove city check)
    // Message: "The zipcode does not match the selected state, please correct or press next to continue."
    const zipData = await getInfoForZip(party(PARTY.addressZip));
    // check zip code
    if (!zipConfirm) {

      NassauLog(zipData);
      if (zipData.state!=party(PARTY.addressState)) {
        setZipConfirm(true);
        setWarningMessage(MYGAStrings.IdDataEntry_InvalidZipCode);
        // errorMessage.current = MYGAStrings.IdDataEntry_InvalidZipCode;
        return false;
      }

      // ACCOUNTS FOR MULTIPLE CITIES
      // if (zipData.localities) {
      //   NassauLog(`The result of the compare is ${zipData.localities.includes(party(PARTY.addressCity))}`)
      //   if (zipData.localities.includes(party(PARTY.addressCity))) {
      //     NassauLog('Its a match (Localities)!')
      //   } else {
      //     //Show warning
      //     NassauLog('This is a warning since none of the cities match the city given (localities).')
      //     setZipConfirm(true);
      //     return false;
      //   }
      // } else {
      //   NassauLog(`The city given: ${PARTY.addressCity}, the expected city: ${zipData.city}`)
      //   if (zipData.city === party(PARTY.addressCity)) {
      //     NassauLog('Its a match!')
      //     return true;
      //   } else {
      //     //Show warning
      //     NassauLog('This is a warning since the single city does not match the city given.')
      //     setZipConfirm(true);
      //     return false;
      //   }
      // }
    }

    return true;
  };

  const saveApplication = useRef(null);
  saveApplication.current = async () => {
    NassauLog("Owner.saveApplication");

    let finalParty = {...party()};

    //Set the issuing state the same as the address state.
    finalParty.issuingState = finalParty.addressState;

    //Remove special character (#)
    if (finalParty.addressStreet) {
      finalParty.addressStreet = finalParty.addressStreet.replace("#", "");
    }

    // Compute the age based on DOB
    finalParty[PARTY.age] = getAgeFromDOB(finalParty,PARTY.dob)
    
    // 2021.10.14.ADivelbiss - Updating this part to save the owner/annuitant in an array
    let nextParties = [...parties];
    nextParties[index] = finalParty;
    
    // Reconcile all relationships
    const reconciled = reconcileParties(nextParties);
    if (reconciled) {
      NassauLog('reconciledParties: ' + JSON.stringify(reconciled, null, 2));
      nextParties = reconciled;
    }
  
    // Update the current application
    // NassauLog('nextParties: ' + JSON.stringify(nextParties, null, 2));
    await mygaSession.setCurrentAppSectionData(APP_SECTION.parties,nextParties);
    
    // sync with the backend
    await syncApplication(APP_SECTION.parties);
  
    // 2022.05.19.ADivelbiss - currently assume all parties applied to all selected products.
    const nextProductDTOs = [...mygaSession.getCurrentAppProductDTOs()];
    nextProductDTOs.forEach(dto=>dto[MM_PRODUCT_DTO.parties]=nextParties);
    mygaSession.setCurrentAppProductDTOs(nextProductDTOs)
    await syncApplication(APP_SECTION.products);
    
    // Clear the current party index
    mygaSession.setCurrentPartyIndex(null);
  
  }

  const generateActionList = useRef(null)
  generateActionList.current = async () => {
    if (process.env.REACT_APP_TESTING === '1' && GENERATOR.getTestModeEnabled()) {
      const handleTrust = () => {
        const dot = new Date(party(PARTY.dateOfTrust))
        return [
          {
            type: 'click',
            id: 'entityTypeTrust'
          },
          {
            type: 'keys',
            id: 'trustName',
            value: party(PARTY.trustName)
          },
          {
            type: 'click',
            id: `dob_month_${dot.getMonth() + 1}`
          },
          {
            type: 'click',
            id: `dob_day_${dot.getDate()}`
          },
          {
            type: 'click',
            id: `dob_year_${dot.getFullYear()}`
          },
          {
            type: 'keys',
            id: 'addressStreet',
            value: party(PARTY.addressStreet)
          },
          {
            type: 'keys',
            id: 'addressCity',
            value: party(PARTY.addressCity)
          },
          {
            type: 'click',
            id: `addressState_${party(PARTY.addressState)}`
          },
          {
            type: 'keys',
            id: 'addressZip',
            value: party(PARTY.addressZip)
          },
        ]
      }

      const handleIndividual = () => {
        const dob = new Date(party(PARTY.dob))
        const initialDob = new Date(getInitialParty(PARTY.dob))
        const idExpire = new Date(party(PARTY.idExpiration))
        const initialIdExpire = new Date(getInitialParty(PARTY.idExpiration))
        const handleTestField = GENERATOR.handleTestField

        let genderID = party(PARTY.gender) === 'Male' ? 'gender_male' : 'gender_female'
        let al = []
        al = handleTestField('keys', 'firstName', party(PARTY.firstName), getInitialParty(PARTY.firstName), al)
        al = handleTestField('keys', 'lastName', party(PARTY.lastName), getInitialParty(PARTY.lastName), al)
        al = handleTestField('click', `dob_month_${dob.getMonth() + 1}`, dob.getMonth() + 1, initialDob.getMonth() + 1, al)
        al = handleTestField('click', `dob_day_${dob.getDate()}`, dob.getDate(), initialDob.getDate(), al)
        al = handleTestField('click', `dob_year_${dob.getFullYear()}`, dob.getFullYear(), initialDob.getFullYear(), al)
        al = handleTestField('click', genderID, party(PARTY.gender), getInitialParty(PARTY.gender), al)
        al = handleTestField('keys', 'govId', party(PARTY.ssnOrTaxId), getInitialParty(PARTY.ssnOrTaxId), al)
        al = handleTestField('keys', 'SECTION_2_HOME_TELEPHONE', party(PARTY.daytimePhone), getInitialParty(PARTY.daytimePhone), al)

        if (isOwner(party)) {
          al = handleTestField('click',
            `memberOfMilitary_Individual_${party(PARTY.activeDutyServicePerson)}`,
            party(PARTY.activeDutyServicePerson),
            getInitialParty(PARTY.activeDutyServicePerson),
            al)
        }
        
        // If we have the joint owner then we need to enter the email field
        if (isJointOwner()) {
          al = handleTestField('keys', 'email', party(PARTY.email), getInitialParty(PARTY.email), al)
        }

        al = handleTestField('click',
          toElementId(`occupation_${party(PARTY.occupation)}`),
          party(PARTY.occupation),
          getInitialParty(PARTY.occupation),
          al)

        if (showRelationToOwner()) {
          al = handleTestField('click',
            toElementId(PARTY.relationshipToOwner + '_' + party(PARTY.relationshipToOwner)),
            party(PARTY.relationshipToOwner),
            getInitialParty(PARTY.relationshipToOwner),
            al)
        }

        if (showRelationToAnnuitant()) {
          al = handleTestField('click',
            toElementId(PARTY.relationshipToAnnuitant + '_' + party(PARTY.relationshipToAnnuitant)),
            party(PARTY.relationshipToAnnuitant),
            getInitialParty(PARTY.relationshipToAnnuitant),
            al)
        }

        al = handleTestField('keys', 'addressStreet', party(PARTY.addressStreet), getInitialParty(PARTY.addressStreet), al)
        al = handleTestField('keys', 'addressCity', party(PARTY.addressCity), getInitialParty(PARTY.addressCity), al)
        al = handleTestField('click', `addressState_${party(PARTY.addressState)}`, party(PARTY.addressState), getInitialParty(PARTY.addressState), al)
        al = handleTestField('keys', 'addressZip', party(PARTY.addressZip), getInitialParty(PARTY.addressZip), al)
        al = handleTestField('click',
          `${PARTY.idType}_${party(PARTY.idType).toLowerCase()}`,
          party(PARTY.idType),
          getInitialParty(PARTY.idType),
          al)
        al = handleTestField('keys', 'idNumber', party(PARTY.idNumber), getInitialParty(PARTY.idNumber), al)
        al = handleTestField('click',
          `idExpiration_month_${idExpire.getMonth() + 1}`,
          idExpire.getMonth() + 1,
          initialIdExpire.getMonth() + 1,
          al)
        al = handleTestField('click',
          `idExpiration_day_${idExpire.getDate()}`,
          idExpire.getDate(),
          initialIdExpire.getDate(),
          al)
        al = handleTestField('click',
          `idExpiration_year_${idExpire.getFullYear()}`,
          idExpire.getFullYear(),
          initialIdExpire.getFullYear(),
          al)

        return al
      }

      let actionList = []
      if (party(PARTY.entityType) === ENTITY_TYPE.trust) {
        actionList.push(...handleTrust())
      } else {
        actionList.push(...handleIndividual())
      }

      await GENERATOR.stageManualActionList(actionList)
    }
  }

  const onNextPressed = async () => {
    await saveApplication.current();
    if (process.env.REACT_APP_TESTING === '1' && GENERATOR.getTestModeEnabled()) {
      await generateActionList.current();
    }
  }

  const handleChange = field => e => {
    // update the value for this field.
    let value = e.target ? e.target.value : e;
    let nextParty = {
      ...party(),
      [field]: value
    };
    
    // get a copy of the parties
    let nextParties = [...parties];
    
    // update the parties
    nextParties[index] = nextParty;
  
    // update the relationships of other parties if needed
    if (field===PARTY.relationshipToOwner) {
      // get the owner from parties and update the reciprocal
      const oIndex = getOwnerIndex();
      if (oIndex>=0) {
        let owner = parties[oIndex];
        const relationship = getReciprocalRelationshipFor(value);
        if (isAnnuitant()) {
          owner[PARTY.relationshipToAnnuitant] = relationship;
        }
        if (isJointOwner()) {
          owner[PARTY.relationshipToJointOwner] = relationship;
        }
        if (isJointAnnuitant()) {
          owner[PARTY.relationshipToJointAnnuitant] = relationship;
        }
        nextParties[oIndex] = owner;
      }
    }
    else if (field===PARTY.relationshipToAnnuitant) {
      // get the annuitant from parties and update the reciprocal
      const aIndex = getAnnuitantIndex();
      if (aIndex>=0) {
        let annuitant = parties[aIndex];
        const relationship = getReciprocalRelationshipFor(value);
        if (isOwner()) {
          annuitant[PARTY.relationshipToOwner] = relationship;
        }
        if (isJointOwner()) {
          annuitant[PARTY.relationshipToJointOwner] = relationship;
        }
        if (isJointAnnuitant()) {
          annuitant[PARTY.relationshipToJointAnnuitant] = relationship;
        }
        nextParties[aIndex] = annuitant;
      }
    }
    // else if (field===PARTY.ssnOrTaxId) {
    //   ssnInput.current.setCustomValidity('');
    // }
    
    // set the parties state
    // NassauLog(`nextParty: ${JSON.stringify(nextParty,null,2)}`);
    setParties(nextParties);
  }

  const renderTextField = (key, changeFunc, maxLength, placeholder, label, pattern = undefined, ref=undefined) => {
    return <React.Fragment>
      <Form.Label className="px-0 mb-0">{label}</Form.Label>
      <Form.Control className="page-input"
                    ref={ref}
                    required
                    type="text"
                    id={key}
                    name={key}
                    value={party(key)}
                    onChange={changeFunc(key)}
                    maxLength={maxLength}
                    pattern={pattern}
                    placeholder={placeholder}/>
    </React.Fragment>
  }

  const renderCheckBox = (label, key, checkedValue, uncheckedValue, handleDisable = ()=>{return false;}) => {
    return <div className={'form-check'}>
      <input className={'form-check-input test-enabled'}
             id={key}
             name={key}
             type="checkbox"
             checked={party(key)===checkedValue}
             onClick={ e => {
               let nextParty = {
                 ...party(),
                 [key]: (e.target.checked ? checkedValue : uncheckedValue)
               };
               let nextParties = [...parties];
               nextParties[index] = nextParty;
               setParties(nextParties);
             }}
             onChange={() =>{}}
             disabled={handleDisable()}
      />
      <label className={'form-check-label'} htmlFor={key}>{label}</label>
    </div>
  }
  
  const renderFirstNameField = (key, changeFunc) => {
    return renderTextField(key, changeFunc, charLimits.nameFirst, 'First Name', 'First Name');
  }

  const renderLastNameField = (key, changeFunc) => {
    return renderTextField(key, changeFunc, charLimits.nameLast, 'Last Name', 'Last Name');
  }

  const renderStreetAddressField = (key, changeFunc) => {
    return renderTextField(key, changeFunc, charLimits.addressStreet, 'Street Address', 'Street Address');
  }

  const renderCityField = (key, changeFunc) => {
    return renderTextField(key, changeFunc, charLimits.addressCity, 'City', 'City');
  }

  const renderZipField = (key, changeFunc) => {
    return renderTextField(key, changeFunc, charLimits.addressZip, 'Zip Code', 'Zip', regex.zip);
  }

  const renderIDNumberField = (key, changeFunc) => {
    return renderTextField(key, changeFunc, charLimits.idNumber, 'ID Number', 'ID Number', regex.idNumber);
  }

  const renderSSNField = (key, changeFuc) => {
    return renderTextField(key, changeFuc,
        charLimits.ssn,
        'Social Security Number',
        'SSN',
        regex.ssn, (el)=>{ssnInput.current = el});
  }

  const renderPhoneNumberField = (key, changeFuc) => {
    return renderTextField(key, changeFuc, charLimits.phoneNumberUS, 'Phone Number', 'Phone Number', regex.phoneNumberUS);
  }

  const renderRelationshipField = (label, key, changeFunc, disabled=false, info=null, ref = undefined) => {
    // NassauLog(`key: ${key} info ${info}`);
    return <Row className="page-data-entry pt-3">
      <Col sm={6} className="pb-2">
        <Form.Label className="px-0 mb-0">{label}</Form.Label>&nbsp;
        {
          info && (
              <PopoverTooltip title={'Relationship'} content={info}/>
          )
        }
      </Col>
      <Col sm={6} className="pb-2">
        <Form.Control required
                      ref={ref}
                      as="select"
                      className="page-select"
                      disabled={disabled}
                      id={key}
                      value={party(key)}
                      onChange={changeFunc(key)}
                      name={key}>
          <option disabled value={PARTY_RELATIONSHIPS.none}>Make a Selection</option>
          {
            Object.values(PARTY_RELATIONSHIPS_SELECT).map((item,index)=>{
              return (
                  <option id={toElementId(key+'_'+item)} key={index} value={item}>{item}</option>
              )
            })
          }
        </Form.Control>
      </Col>
    </Row>
  }
  
  const renderOccupationFieldHorizontal = () => {
    return <Row className="page-data-entry pt-3">
      <Col sm={6}>
          <Form.Label className="px-0 mb-0">
            {Resources.Strings.IdFinish_OccupationLabel}
          </Form.Label>
          {/*<PopoverTooltip*/}
          {/*    className={'d-inline-block'}*/}
          {/*    title={''}*/}
          {/*    content={'If retired, please choose your former occupation.'}>*/}
          {/*  <div className={'retired-link'}>*/}
          {/*    &nbsp;Retired?*/}
          {/*  </div>*/}
          {/*</PopoverTooltip>*/}
      </Col>
      <Col sm={6} className={"pb-2"}>
        <Form.Control id={PARTY.occupation}
                      name={PARTY.occupation}
                      required
                      as="select"
                      className="page-select"
                      value={party(PARTY.occupation)}
                      onChange={handleChange(PARTY.occupation)}
        >
          <option disabled value="">Make a Selection</option>
          {
            getCommonOccupations().map((item,index)=>{
              return (
                  <option key={index}
                          id={toElementId(`occupation_${item.toLowerCase()}`)}
                          value={item.toUpperCase()}>{item}</option>
              )
            })
          }
        </Form.Control>
        {
          party(PARTY.occupation)==='OTHER' && (
              <Row className="pr-0 pl-0 pt-2">
                <Col className="pl-0 pr-0">
                  <Form.Control
                      name={PARTY.occupation+'_OTHER'}
                      required
                      as="select"
                      className="page-select"
                      value={party(PARTY.occupationOther)}
                      onChange={handleChange(PARTY.occupationOther)}
                  >
                    <option disabled value="">Make a Selection</option>
                    {
                      getOtherOccupations().map((item,index)=>{
                        return (
                            <option key={index} value={item.toUpperCase()}>{item}</option>
                        )
                      })
                    }
                  </Form.Control>
                </Col>
              </Row>
          )
        }
      </Col>
    </Row>
    // return <Row className="page-data-entry pt-3">
    //   <Col sm={6} className="pb-2">
    //     <Form.Label className="px-0 mb-0">
    //       {Resources.Strings.IdFinish_OccupationLabel}
    //     </Form.Label>
    //     <PopoverTooltip
    //         className={'d-inline-block'}
    //         title={''}
    //         content={'If retired, please choose your former occupation.'}>
    //       <div className={'retired-link'}>
    //         &nbsp;Retired?
    //       </div>
    //     </PopoverTooltip>
    //   </Col>
    //   <Col sm={6} className="pb-2">
    //     {/*<Row className="pr-0 pl-0 pb-1">*/}
    //     {/*  <Col className="pl-0 pr-0">*/}
    //     <Form.Control id={PARTY.occupation}
    //                       name={PARTY.occupation}
    //                       required
    //                       as="select"
    //                       className="page-select"
    //                       value={party(PARTY.occupation)}
    //                       onChange={handleChange(PARTY.occupation)}
    //         >
    //           <option disabled value="">Make a Selection</option>
    //           {
    //             getCommonOccupations().map((item,index)=>{
    //               return (
    //                   <option key={index} value={item.toUpperCase()}>{item}</option>
    //               )
    //             })
    //           }
    //     </Form.Control>
    //     {/*  </Col>*/}
    //     {/*</Row>*/}
    //     {
    //       party(PARTY.occupation)==='OTHER' && (
    //           <Row className="pr-0 pl-0 pt-2">
    //             <Col className="pl-0 pr-0">
    //               <Form.Control
    //                   name={PARTY.occupation+'_OTHER'}
    //                   required
    //                   as="select"
    //                   className="page-select"
    //                   value={party(PARTY.occupationOther)}
    //                   onChange={handleChange(PARTY.occupationOther)}
    //               >
    //                 <option disabled value="">Make a Selection</option>
    //                 {
    //                   getOtherOccupations().map((item,index)=>{
    //                     return (
    //                         <option key={index} value={item.toUpperCase()}>{item}</option>
    //                     )
    //                   })
    //                 }
    //               </Form.Control>
    //             </Col>
    //           </Row>
    //       )
    //     }
    //   </Col>
    // </Row>
    // return <Row className="page-data-entry pt-3">
    //   <Col sm={6} className="pb-2">
    //     <Form.Label className="px-0 mb-0">
    //       {Resources.Strings.IdFinish_OccupationLabel}
    //       <PopoverTooltip
    //         title={''}
    //         content={'If retired, please choose your former occupation.'}>
    //         <div className={'retired-link'}>
    //           Retired?
    //         </div>
    //       </PopoverTooltip>
    //     </Form.Label>
    //   </Col>
    //   <Col sm={6} className="pb-2">
    //     <Row className="pr-0 pl-0 pb-1">
    //       <Col className="pl-0 pr-0">
    //         <Form.Control id={PARTY.occupation}
    //                       name={PARTY.occupation}
    //                       required
    //                       as="select"
    //                       className="page-select"
    //                       value={party(PARTY.occupation)}
    //                       onChange={handleChange(PARTY.occupation)}
    //         >
    //           <option disabled value="">Make a Selection</option>
    //           {
    //             getCommonOccupations().map((item,index)=>{
    //               return (
    //                 <option key={index} value={item.toUpperCase()}>{item}</option>
    //               )
    //             })
    //           }
    //         </Form.Control>
    //       </Col>
    //     </Row>
    //     {
    //       party(PARTY.occupation)==='OTHER' && (
    //         <Row className="pr-0 pl-0 pt-2">
    //           <Col className="pl-0 pr-0">
    //             <Form.Control
    //               name={PARTY.occupation+'_OTHER'}
    //               required
    //               as="select"
    //               className="page-select"
    //               value={party(PARTY.occupationOther)}
    //               onChange={handleChange(PARTY.occupationOther)}
    //             >
    //               <option disabled value="">Make a Selection</option>
    //               {
    //                 getOtherOccupations().map((item,index)=>{
    //                   return (
    //                     <option key={index} value={item.toUpperCase()}>{item}</option>
    //                   )
    //                 })
    //               }
    //             </Form.Control>
    //           </Col>
    //         </Row>
    //       )
    //     }
    //   </Col>
    // </Row>

  }
  
  const renderOccupationFieldVertical = () => {
    return <React.Fragment>
      <Form.Label className="px-0 mb-0">
        {Resources.Strings.IdFinish_OccupationLabel}
      </Form.Label>
      {/*<PopoverTooltip*/}
      {/*    className={'d-inline-block'}*/}
      {/*    title={''}*/}
      {/*    content={'If retired, please choose your former occupation.'}>*/}
      {/*  <div className={'retired-link'}>*/}
      {/*    &nbsp;Retired?*/}
      {/*  </div>*/}
      {/*</PopoverTooltip>*/}
      <Row className="pr-0 pl-0 pb-1">
        <Col className="pl-0 pr-0">
          <Form.Control id={PARTY.occupation}
                        name={PARTY.occupation}
                        required
                        as="select"
                        className="page-select"
                        value={party(PARTY.occupation)}
                        onChange={handleChange(PARTY.occupation)}
          >
            <option disabled value="">Make a Selection</option>
            {
              getCommonOccupations().map((item,index)=>{
                return (
                    <option key={index} value={item.toUpperCase()}>{item}</option>
                )
              })
            }
          </Form.Control>
        </Col>
      </Row>
      {
        party(PARTY.occupation)==='OTHER' && (
            <Row className="pr-0 pl-0 pt-2">
              <Col className="pl-0 pr-0">
                <Form.Control
                    name={PARTY.occupation+'_OTHER'}
                    required
                    as="select"
                    className="page-select"
                    value={party(PARTY.occupationOther)}
                    onChange={handleChange(PARTY.occupationOther)}
                >
                  <option disabled value="">Make a Selection</option>
                  {
                    getOtherOccupations().map((item,index)=>{
                      return (
                          <option key={index} value={item.toUpperCase()}>{item}</option>
                      )
                    })
                  }
                </Form.Control>
              </Col>
            </Row>
        )
      }
    </React.Fragment>
    // return <Row className="page-data-entry pt-3">
    //   <Col sm={6} className="pb-2">
    //     <Form.Label className="px-0 mb-0">
    //       {Resources.Strings.IdFinish_OccupationLabel}
    //       <PopoverTooltip
    //         title={''}
    //         content={'If retired, please choose your former occupation.'}>
    //         <div className={'retired-link'}>
    //           Retired?
    //         </div>
    //       </PopoverTooltip>
    //     </Form.Label>
    //   </Col>
    //   <Col sm={6} className="pb-2">
    //     <Row className="pr-0 pl-0 pb-1">
    //       <Col className="pl-0 pr-0">
    //         <Form.Control id={PARTY.occupation}
    //                       name={PARTY.occupation}
    //                       required
    //                       as="select"
    //                       className="page-select"
    //                       value={party(PARTY.occupation)}
    //                       onChange={handleChange(PARTY.occupation)}
    //         >
    //           <option disabled value="">Make a Selection</option>
    //           {
    //             getCommonOccupations().map((item,index)=>{
    //               return (
    //                 <option key={index} value={item.toUpperCase()}>{item}</option>
    //               )
    //             })
    //           }
    //         </Form.Control>
    //       </Col>
    //     </Row>
    //     {
    //       party(PARTY.occupation)==='OTHER' && (
    //         <Row className="pr-0 pl-0 pt-2">
    //           <Col className="pl-0 pr-0">
    //             <Form.Control
    //               name={PARTY.occupation+'_OTHER'}
    //               required
    //               as="select"
    //               className="page-select"
    //               value={party(PARTY.occupationOther)}
    //               onChange={handleChange(PARTY.occupationOther)}
    //             >
    //               <option disabled value="">Make a Selection</option>
    //               {
    //                 getOtherOccupations().map((item,index)=>{
    //                   return (
    //                     <option key={index} value={item.toUpperCase()}>{item}</option>
    //                   )
    //                 })
    //               }
    //             </Form.Control>
    //           </Col>
    //         </Row>
    //       )
    //     }
    //   </Col>
    // </Row>
  }
  
  const renderEmailField = () => {
    return <React.Fragment>
      <Form.Label className="px-0 mb-0">Email</Form.Label>
      <Form.Control id={PARTY.email}
                    name={PARTY.email}
                    required
                    disabled={isOwner()}
                    type="text"
                    className="page-input"
                    value={party(PARTY.email)}
                    pattern={regex.email}
                    onChange={handleChange(PARTY.email)}
      />
    </React.Fragment>
  }

  const renderIdentityField = (key, changeFunc) => {
    // const idRadioYes = `${key}_${party(PARTY.entityType)}_Yes`
    // const idRadioNo = `${key}_${party(PARTY.entityType)}_No`
    const appData = compAppData();
    // NassauLog(`\nApp Data Test: ${JSON.stringify(appData, null, 2)}\n`);
    const disableOwnerCheckbox = isOwner;
    const disableJointOwnerCheckbox = isNotAnAnnuitant;
    const disableAnnuitantCheckbox = () => (isNotAnOwner() || (isOwner() && isIRA()));
    const disableJointAnnuitantCheckbox = isNotAnOwner;
    const disable = ((disableOwnerCheckbox()||disableJointOwnerCheckbox())&&(disableAnnuitantCheckbox()||disableJointAnnuitantCheckbox()));
    return <React.Fragment>
      {
        !disable && <Container>
        <Row className="page-data-entry py-4">
          <Col sm={6} className="py-2">
            <Form.Label className="px-0 mb-0">
              {Resources.Strings.IdFinish_QuestionRoles}&nbsp;
              <PopoverTooltip content={
                <div>
                  {/*<p>{Resources.Strings.IdFinish_QuestionRolesInfo}</p>*/}
                  <div><b>Owner: </b>{Resources.Strings.IdFinish_QuestionRolesOwnerInfo}</div>
                  <div><b>Joint Owner: </b>{Resources.Strings.IdFinish_QuestionRolesJointOwnerInfo}</div>
                  <div><b>Annuitant: </b>{Resources.Strings.IdFinish_QuestionRolesAnnuitantInfo}</div>
                </div>
              }/>
            </Form.Label>
          </Col>
          <Col sm={6} className="pb-2 my-auto">
            <Row className="pl-0 pr-0">
              {
                isOwner() || canAddOwner(appData) ? (
                    <Col sm={6} className="py-2 pb-sm-0">
                      {
                        renderCheckBox(
                            "Owner",
                            PARTY.ownerType,
                            OWNER_TYPE.primary,
                            OWNER_TYPE.none,
                            disableOwnerCheckbox
                        )
                      }
                    </Col>
                ) : isJointOwner() || canAddJointOwner(appData) ? (
                    <Col sm={6} className="py-2 pb-sm-0">
                      {
                        renderCheckBox(
                            "Joint Owner",
                            PARTY.ownerType,
                            OWNER_TYPE.joint,
                            OWNER_TYPE.none,
                            disableJointOwnerCheckbox)
                      }
                    </Col>
                ) : (
                    <></>
                )
              }
              {
                isAnnuitant() || canAddAnnuitant(appData) ? (
                    <Col sm={6} className="py-2 pb-sm-0">
                      {
                        renderCheckBox(
                            "Annuitant",
                            PARTY.annuitantType,
                            ANNUITANT_TYPE.primary,
                            ANNUITANT_TYPE.none,
                            disableAnnuitantCheckbox
                        )
                      }
                    </Col>
                ) : isJointAnnuitant() || canAddJointAnnuitant(appData) ? (
                    <Col sm={6} className="py-2 pb-sm-0">
                      {
                        renderCheckBox(
                            "Joint Annuitant",
                            PARTY.annuitantType,
                            ANNUITANT_TYPE.joint,
                            ANNUITANT_TYPE.none,
                            disableJointAnnuitantCheckbox
                        )
                      }
                    </Col>
                ) : (<></>)
              }
            </Row>
          </Col>
        </Row>
      </Container>
      }
    </React.Fragment>
  }

  const renderMilitaryField = (key, changeFunc) => {
    const idRadioYes = `${key}_${party(PARTY.entityType)}_Yes`
    const idRadioNo = `${key}_${party(PARTY.entityType)}_No`
    return <Row className="page-data-entry">
      <Col sm={6} className="pb-2">
        <Form.Label className="px-0 mb-0">
          {Resources.Strings.IdFinish_QuestionMilitaryMember}&nbsp;
          <PopoverTooltip title={'Active Duty Military'}
                          content={'If the Owner or Annuitant is active duty military, we are required to provide additional disclosure with the application.'}/>
        </Form.Label>
      </Col>
      <Col sm={6} className="pb-2 my-auto">
        <Row className="pl-0 pr-0">
          <Col xs={6} className="pl-0">
            <div className='form-check form-check-inline '>
              <input required type="radio" className="form-check-input"
                     id={idRadioYes}
                     name={key}
                     checked={(party(key) === 'Yes')}
                     onClick={changeFunc(key)}
                     onChange={() => {}}
                     value="Yes"/>
              <label className="form-check-label"
                     htmlFor={idRadioYes}>Yes</label>
            </div>
          </Col>
          <Col xs={6}>
            <div className='form-check form-check-inline '>
              <input required type="radio" className="form-check-input"
                     id={idRadioNo}
                     name={key}
                     checked={(party(key) === 'No')}
                     onClick={changeFunc(key)}
                     onChange={() => {}}
                     value="No"/>
              <label className="form-check-label" htmlFor={idRadioNo}>No</label>
            </div>
          </Col>
        </Row>
      </Col>
    </Row>
  }

  const renderIndividual = () => {
    return (
        <>
          {/* Name, DOB, and Gender */}
          <Container>
            <Row className="page-data-entry">
              <Col sm={6} className="pb-2 pb-sm-0">
                {renderFirstNameField(PARTY.firstName, handleChange)}
              </Col>
              <Col sm={6} className="pb-2 pb-sm-0">
                {renderLastNameField(PARTY.lastName, handleChange)}
              </Col>
            </Row>
            <Row className="page-data-entry">
              {/* DOB */}
              <Col md={6} className="pb-2 pb-sm-0">
                <Form.Label className="px-0 mb-0">Date Of Birth</Form.Label>
                <NSREDatePicker
                    ref={(el) => {
                      dobInput.current = el;
                    }}
                    className={'form-control page-input'}
                    value={
                      party(PARTY.dob) && party(PARTY.dob).length > 0 ?
                          party(PARTY.dob) :
                          moment().subtract(60, 'years').format(formatting.dob)
                    }
                    onChange={(value) => {
                      dobInput.current.setCustomValidity('');
                      handleChange(PARTY.dob)(value)
                    }}
                    onClick={(value) => {
                      dobInput.current.setCustomValidity('');
                      handleChange(PARTY.dob)(value)
                    }}
                    name={PARTY.dob}
                    minDate={moment().subtract(85, "years").toDate()}
                    maxDate={moment().subtract(18, "years").toDate()}
                    dataFormat={formatting.dob}
                    yearOrderAscending={false}
                    required
                />
                <Form.Control
                    ref={(el) => {
                      dobInputValidation.current = el;
                    }}
                    className=""
                    style={{display: 'none'}}
                    required
                    type="text"
                    value={
                      party(PARTY.dob)
                    }
                    onChange={() => {
                      NassauLog('dobInputValidation.onChange')
                    }}
                />
              </Col>
              {/* Gender */}
              <Col md={6} className="pb-2 pb-sm-0">
                <Form.Label className="px-0 mb-0">Gender</Form.Label>
                <Form.Control className="page-select"
                              required
                              as="select"
                              id={PARTY.gender}
                              name={PARTY.gender}
                              value={party(PARTY.gender)}
                              onChange={handleChange(PARTY.gender)}>
                  <option disabled value="">Select Your Gender</option>
                  <option id={'gender_male'} value="Male">Male</option>
                  <option id={'gender_female'} value="Female">Female</option>
                </Form.Control>
              </Col>
            </Row>
            <Row className="page-data-entry">
              <Col sm={6} className="pb-2 pb-sm-0">
                {renderSSNField(PARTY.ssnOrTaxId, handleChange)}
              </Col>
              <Col sm={6} className="pb-2 pb-sm-0">
                {renderPhoneNumberField(PARTY.daytimePhone, handleChange)}
              </Col>
            </Row>
            <Row className="page-data-entry">
              {
                (isOwner() || isJointOwner()) && (
                    <Col md={6} className={'pb-2 pb-sm-0'}>
                      {renderEmailField()}
                    </Col>
                )
              }
            </Row>
            {/* divider */}
            <div className="page-hr"/>

            {
              (isOwner()) &&
              renderMilitaryField(PARTY.activeDutyServicePerson, handleChange)
            }
            {
              renderOccupationFieldHorizontal()
            }
            {
              (showRelationToOwner()) &&
              renderRelationshipField(
                  (compHasOwnerSameAsAnnuitant() ? 'Relationship to Owner/Annuitant' : 'Relationship to Owner'),
                  PARTY.relationshipToOwner,
                  handleChange,
                  isJointOwner() && hasOwnerAsIndividual(compAppData()),
                  isJointOwner() && hasOwnerAsIndividual(compAppData()) ? MYGAStrings.JOtoOSpouse : null,
                  (el)=>{relationshipToOwnerInput.current = el}
              )
            }
            {
              (showRelationToAnnuitant()) &&
              renderRelationshipField(
                  'Relationship to Annuitant',
                  PARTY.relationshipToAnnuitant,
                  handleChange,
                  false,
                  null,
                  (el)=>{relationshipToAnnuitantInput.current = el}
              )
            }

          </Container>
      
          {/* divider */}
          <div className="page-hr"/>
      
          {/* Address, ID */}
          <Container className="">
            <Row className="page-data-entry">
              {/* address */}
              <Col sm={6} className="pb-2 pb-sm-0">
                <Row className="page-data-entry">
                  <Col className={"p-0"}>
                    {renderStreetAddressField(PARTY.addressStreet, handleChange)}
                  </Col>
                </Row>
                <Row className="page-data-entry">
                  <Col className={"p-0"}>
                    {renderCityField(PARTY.addressCity, handleChange)}
                  </Col>
                </Row>
                <Row className="page-data-entry">
                  <Col className={"p-0"}>
                    <Form.Label className="px-0 mb-0">State</Form.Label>
                    <Form.Control required
                                  className="page-select"
                                  as="select"
                                  id={PARTY.addressState}
                                  name={PARTY.addressState}
                                  disabled={isFL() && isOwner()}
                                  value={party(PARTY.addressState)}
                                  onChange={handleChange(PARTY.addressState)}
                                  title="State">
                      <option disabled value="">Select a State</option>
                      {
                        AVAILABLE_STATES.filter(st=> !ENABLE_FLORIDA_FLOW || !isOwner() || st.abbreviation!=="FL" || isFL()).map(state => {
                          return (
                              <option key={state.abbreviation}
                                      id={`addressState_${state.abbreviation}`}
                                      value={state.abbreviation}>{state.abbreviation}</option>
                          )
                        })
                      }
                    </Form.Control>
                  </Col>
                </Row>
                <Row className="page-data-entry">
                  <Col className={"p-0"}>
                    {renderZipField(PARTY.addressZip, handleChange)}
                  </Col>
                </Row>
              </Col>
              {/* ID */}
              <Col sm={6} className="pb-2 pb-sm-0">
                <Row className="page-data-entry">
                  <Col className={"p-0"}>
                    <Form.Label className="px-0 mb-0">Type of ID</Form.Label>
                    <Form.Control required
                                  as="select"
                                  className="page-select"
                                  id={PARTY.idType}
                                  value={party(PARTY.idType)}
                                  onChange={handleChange(PARTY.idType)}
                                  name={PARTY.idType}>
                      <option disabled value="">Select an ID Type</option>
                      <option id={`${PARTY.idType}_drivers_license_or_id`} value="DRIVERS_LICENSE_OR_ID">Driver's License</option>
                      <option id={`${PARTY.idType}_us_passport`} value="US_PASSPORT">U.S. Passport</option>
                    </Form.Control>
                  </Col>
                </Row>
                <Row className="page-data-entry">
                  <Col className={"p-0"}>
                    {renderIDNumberField(PARTY.idNumber, handleChange)}
                  </Col>
                </Row>
                <Row className="page-data-entry">
                  <Col className={"p-0"}>
                    <Form.Label className="px-0 mb-0">Expiration Date</Form.Label>
                    <NSREDatePicker
                        ref={(el) => {
                          idExpirationInput.current = el;
                        }}
                        className={'form-control page-input'}
                        required
                        value={party(PARTY.idExpiration)}
                        onChange={(value) => {
                          idExpirationInput.current.setCustomValidity('');
                          handleChange(PARTY.idExpiration)(value)
                        }}
                        onClick={(value) => {
                          idExpirationInput.current.setCustomValidity('');
                          handleChange(PARTY.idExpiration)(value)
                        }}
                        minDate={moment().subtract(0, "years").toDate()}
                        maxDate={moment().add(10, "years").toDate()}
                        name={PARTY.idExpiration}
                        dataFormat={formatting.idExpiration}
                        placeholder="Expiration"
                    />
                    <Form.Control
                        ref={(el) => {
                          idExpirationInputValidation.current = el;
                        }}
                        className=""
                        style={{display: 'none'}}
                        required
                        type="text"
                        value={
                          party(PARTY.idExpiration)
                        }
                        onChange={() => {
                          NassauLog('idExpirationInputValidation.onChange')
                        }}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
          </Container>
        </>
    )
  }
  
  const renderTrust = () => {
    return (
        <>
          {/* Name, DOB, and Gender */}
          <Container>
            <Row className="page-data-entry">
              <Col className="pb-2 pb-sm-0">
                {renderTextField(PARTY.trustName, handleChange, charLimits.trustName, 'Trust Name', 'Trust Name')}
              </Col>
            </Row>
            <Row className="page-data-entry">
              {/* DOB */}
              <Col md={6} className="pb-2 pb-sm-0">
                <Form.Label className="px-0 mb-0">Date Of Trust</Form.Label>
                <NSREDatePicker
                    ref={(el) => {
                      dotInput.current = el;
                    }}
                    className={'form-control page-input'}
                    value={
                      party(PARTY.dateOfTrust) && party(PARTY.dateOfTrust).length > 0 ?
                          party(PARTY.dateOfTrust) :
                          moment().subtract(60, 'years').format(formatting.dob)
                    }
                    onChange={(value) => {
                      dotInput.current.setCustomValidity('');
                      handleChange(PARTY.dateOfTrust)(value)
                    }}
                    onClick={(value) => {
                      dotInput.current.setCustomValidity('');
                      handleChange(PARTY.dateOfTrust)(value)
                    }}
                    name={PARTY.dateOfTrust}
                    minDate={moment().subtract(85, "years").toDate()}
                    maxDate={moment().toDate()}
                    dataFormat={formatting.dob}
                    yearOrderAscending={false}
                    required
                />
                <Form.Control
                    ref={(el) => {
                      dotInputValidation.current = el;
                    }}
                    className=""
                    style={{display: 'none'}}
                    required
                    type="text"
                    value={
                      party(PARTY.dateOfTrust)
                    }
                    onChange={() => {
                      NassauLog('dotInputValidation.onChange')
                    }}
                />
              </Col>
            </Row>
            {
              (compHasOwner() && !isOwner()) &&
              renderRelationshipField(
                  'Relationship to Owner',
                  PARTY.relationshipToOwner,
                  handleChange,
                  isJointOwner() && hasOwnerAsIndividual(),
                  null,
                  (el)=>{relationshipToOwnerInput.current = el}
              )
            }
            {
              (compHasAnnuitant() && !isAnnuitant()) &&
              renderRelationshipField(
                  'Relationship to Annuitant',
                  PARTY.relationshipToAnnuitant,
                  handleChange,
                  false,
                  null,
                  (el)=>{relationshipToAnnuitantInput.current = el}
              )
            }
          </Container>
          
          {/* divider */}
          <div className="page-hr"/>
          
          {/* Address, ID */}
          <Container className="">
            <Row className="page-data-entry">
              {/* address */}
              <Col sm={12} className="pb-2 pb-sm-0">
                <Row className="page-data-entry">
                  <Col className={"p-0"}>
                    {renderStreetAddressField(PARTY.addressStreet, handleChange)}
                  </Col>
                </Row>
                <Row className="page-data-entry">
                  <Col className={"p-0"}>
                    {renderCityField(PARTY.addressCity, handleChange)}
                  </Col>
                </Row>
                <Row className="page-data-entry">
                  <Col className={"p-0"}>
                    <Form.Label className="px-0 mb-0">State</Form.Label>
                    <Form.Control required
                                  className="page-select"
                                  as="select"
                                  id={PARTY.addressState}
                                  name={PARTY.addressState}
                                  value={party(PARTY.addressState)}
                                  onChange={handleChange(PARTY.addressState)}
                                  title="State">
                      <option disabled value="">Select a State</option>
                      {
                        AVAILABLE_STATES.filter(st=> !ENABLE_FLORIDA_FLOW || !isOwner() || st.abbreviation!=="FL" || isFL()).map(state => {
                          return (
                              <option key={state.abbreviation}
                                      id={`addressState_${state.abbreviation}`}
                                      value={state.abbreviation}>{state.abbreviation}</option>
                          )
                        })
                      }
                    </Form.Control>
                  </Col>
                </Row>
                <Row className="page-data-entry">
                  <Col className={"p-0"}>
                    {renderZipField(PARTY.addressZip, handleChange)}
                  </Col>
                </Row>
              </Col>
            </Row>
          </Container>
        </>
    )
  }
  
  const backLabel = isEditing ? Resources.Strings.General_ButtonCancelLabel: Resources.Strings.General_ButtonBackLabel;
  const onBackPressed = isEditing ?
      async () =>  {
        // Clear the current party index
        await mygaSession.setCurrentPartyIndex(null);
      } :
      saveApplication.current;
  const nextLabel = isEditing ? Resources.Strings.General_ButtonOKLabel : Resources.Strings.General_ButtonForwardLabel;
  
  
  return <AppContentWrapper
    prevLabel={backLabel}
    onBackPressed={onBackPressed}
    nextLabel={nextLabel}
    onNextPressed={onNextPressed}
    validateOnNext={validate}
    updatePage={props.updatePage}
    errorMessage={warningMessage || errorMessage.current}>


    <form className={'needs-validation'}
          ref={(form) => {
            pageForm.current = form;
          }}>
      
      {/* Page Title */}
      <Container>
        <Row>
          <Col className={'text-center'}>
            <div className={'page-section-title'}>{typeForParty(party())}</div>
          </Col>
        </Row>
      </Container>
  
      {renderIdentityField()}
      
      {/* handle owner tabs for individual/trust */}
      {
        (isOwner() && !isUsingQualifiedFunds()) ? (
            <Container>
              <Tabs
                  activeKey={party(PARTY.entityType)}
                  onSelect={handleChange(PARTY.entityType)}>
                <Tab id={'entityTypeIndividual'} eventKey={ENTITY_TYPE.individual} title={'Individual'}>
                  {
                    party(PARTY.entityType)===ENTITY_TYPE.individual ?  renderIndividual() : <></>
                  }
                </Tab>
                <Tab id={'entityTypeTrust'} eventKey={ENTITY_TYPE.trust} title={'Trust'}>
                  {
                    party(PARTY.entityType)===ENTITY_TYPE.trust ? renderTrust() : <></>
                  }
                </Tab>
              </Tabs>
            </Container>
        ) : renderIndividual()
      }
      
    </form>
    
  </AppContentWrapper>
}

export default OwnerAnnuitant;


// <Col sm={6} className="py-2 pb-sm-0">
//   {
//     /**
//      * hasOwner isOwner hasJointOwner  isJointOwner  Show
//      *  no      no      x              no            Owner checkbox
//      *  yes     yes     x              no            Owner checkbox
//      *  yes     no      yes            no            Owner checkbox disabled?
//      *  x       no      yes            yes           Joint Owner checkbox
//      *  yes     no      no             no            Joint Owner checkbox
//      *
//      *  no      yes     x              x             invalid state
//      *  x       x       no             yes           invalid state
//      *  x       yes     x              yes           invalid state
//      */
//     (isOwner() || (!hasOwner() && !hasJointOwner())) ? (
//         renderCheckBox(
//             "Owner",
//             PARTY.ownerType,
//             OWNER_TYPE.primary,
//             OWNER_TYPE.none,()=>{return true;})
//     ) : (!isUsingQualifiedFunds() && (isJointOwner() || (hasOwner() && !hasJointOwner()))) ?
//         (
//             renderCheckBox(
//                 "Joint Owner",
//                 PARTY.ownerType,
//                 OWNER_TYPE.joint,
//                 OWNER_TYPE.none,
//                 isNotAnAnnuitant)
//         ) : (<></>)
//
//   }
//
// </Col>
// <Col sm={6} className="py-2 pb-sm-0">
//   {
//     /**
//      * hasAnnuitant isAnnuitant hasJointAnnuitant  isJointAnnuitant  Show
//      *  no          no          x                  no                Annuitant checkbox
//      *  yes         yes         x                  no                Annuitant checkbox
//      *  yes         no          yes                no                Annuitant checkbox disabled?
//      *  x           no          yes                yes               Joint Annuitant checkbox
//      *  yes         no          no                 no                Joint Annuitant checkbox
//      *
//      *  no          yes         x                  x                 invalid state
//      *  x           x           no                 yes               invalid state
//      *  x           yes         x                  yes               invalid state
//      */
//     (isAnnuitant() || (!hasAnnuitant() && !hasJointAnnuitant())) ? (
//         renderCheckBox(
//             "Annuitant",
//             PARTY.annuitantType,
//             ANNUITANT_TYPE.primary,
//             ANNUITANT_TYPE.none,
//             isNotAnOwner)
//     ) : (isJointAnnuitant() || (hasAnnuitant() && !hasJointAnnuitant())) ?
//         (
//             renderCheckBox(
//                 "Joint Annuitant",
//                 PARTY.annuitantType,
//                 ANNUITANT_TYPE.joint,
//                 ANNUITANT_TYPE.none,
//                 isNotAnOwner)
//         ) : (<></>)
//   }
// </Col>